// Div (clock-synced rate) option definitions — single source of truth for both
// editors (MDL-288, spec 131661837 §1). Lifted out of the duplicated Effect.vue
// data blocks.
//
// Two lists:
//   NOTE_VALUE_DIVISIONS  — the offered note-value model, shown when the device
//                           advertises the `note_divisions` capability. Values
//                           are beat-anchored ("…b") below the bar and bar
//                           multiples ("1".."16") above it, strictly ascending.
//   LEGACY_RATE_DIVISIONS — the pre-rework bar-fraction options, shown against
//                           firmware that lacks the capability (no mixed state).
//
// Each note-value entry carries:
//   value  — the config string written to the device
//   label  — British caption; the knob's aria-label, and the readout text for
//            bar entries (note entries render the glyph only) (spec §1)
//   tip    — hover description, British / American (e.g. "crotchet / quarter
//            note"); shown on the readout and listed in the full-scale tooltip
//   glyph  — NoteGlyph descriptor (absent for bar entries, which are text-only)

export const LEGACY_RATE_DIVISIONS = [
  { value: '1/16', label: '1/16' },
  { value: '1/12', label: '1/16T' },
  { value: '1/8',  label: '1/8'  },
  { value: '1/6',  label: '1/8T'  },
  { value: '1/4',  label: '1/4'  },
  { value: '1/3',  label: '1/4T'  },
  { value: '1/2',  label: '1/2'  },
  { value: '1',    label: '1 Bar' },
  { value: '2',    label: '2 Bars' },
  { value: '4',    label: '4 Bars' },
  { value: '8',    label: '8 Bars' },
  { value: '12',   label: '12 Bars' },
  { value: '16',   label: '16 Bars' },
];

// Offered note values are curated: those that behave well as an accent cycle
// in every meter. The dotted family, the minim triplet and anything longer
// than a minim are intentionally omitted.
//
// The minim ("2b") was omitted too until MDL-365. It is the half-bar of 4/4 —
// 0.6.3's "1/2" — and without it the sweep jumped from one beat straight to a
// whole bar. Bar supremacy renders it as 2+1 in 3/4, which is what a half-bar
// setting should do there.
//
// This must mirror the firmware's RATE_DIVISION_ORDER (app/lib/midulate/effect.py),
// which is also the enum an incoming CC sweeps — the two lists cannot drift.
export const NOTE_VALUE_DIVISIONS = [
  { value: '1/4b', label: 'semiquaver · ¼ beat',        tip: 'semiquaver / sixteenth note',
    glyph: { notehead: 'filled', stem: true, flags: 2 } },
  { value: '1/3b', label: 'quaver triplet · ⅓ beat',    tip: 'quaver triplet / eighth-note triplet',
    glyph: { notehead: 'filled', stem: true, flags: 1, triplet: true } },
  { value: '1/2b', label: 'quaver · ½ beat',            tip: 'quaver / eighth note',
    glyph: { notehead: 'filled', stem: true, flags: 1 } },
  { value: '2/3b', label: 'crotchet triplet · ⅔ beat',  tip: 'crotchet triplet / quarter-note triplet',
    glyph: { notehead: 'filled', stem: true, triplet: true } },
  { value: '1b',   label: 'crotchet · 1 beat',          tip: 'crotchet / quarter note',
    glyph: { notehead: 'filled', stem: true } },
  { value: '2b',   label: 'minim · 2 beats',            tip: 'minim / half note',
    glyph: { notehead: 'open', stem: true } },
  { value: '1',    label: '1 bar',   tip: '1 bar' },
  { value: '2',    label: '2 bars',  tip: '2 bars' },
  { value: '4',    label: '4 bars',  tip: '4 bars' },
  { value: '8',    label: '8 bars',  tip: '8 bars' },
  { value: '12',   label: '12 bars', tip: '12 bars' },
  { value: '16',   label: '16 bars', tip: '16 bars' },
];

// Pick the option list for a connected device based on its capabilities object
// ({ capabilities: { note_divisions: true } }). Absent capability -> legacy.
export function rateDivisionOptions(capabilities) {
  const caps = capabilities?.capabilities ?? capabilities ?? {};
  return caps.note_divisions ? NOTE_VALUE_DIVISIONS : LEGACY_RATE_DIVISIONS;
}
